import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { API_BASE_URL } from '../../config/constants';

export interface UserCredentialResponse {
  uuid: string;
  userId: string;
  role: string;
  name: string | null;
  hasApiKey: boolean;
  hasBasicCredential: boolean;
  expiresAt: string;
  isDeleted: boolean;
  deletedAt: string | null;
  createdAt: string | null;
  createdBy: string | null;
}

export interface GenerateApiKeyResponse {
  userId: string;
  uuid: string;
  name: string;
  apiKey: string;
  expiresAt: string;
  message: string;
}

export interface LoginHistory {
  userId: string;
  loginDate: string;
  loginType: string;
  requestsCount: number;
  createdAt: string | null;
  lastRequestAt: string | null;
}

export interface LoginHistoryResponse {
  total: number;
  page: number;
  size: number;
  records: LoginHistory[];
}

@Injectable({ providedIn: 'root' })
export class UserApiCredentialsService {

  private readonly base = API_BASE_URL;

  constructor(private http: HttpClient) {}

  /** List credential records – regular users see only their own. */
  listMyCredentials(userId?: string): Observable<UserCredentialResponse[]> {
    const params = userId ? new HttpParams().set('user_id', userId) : undefined;
    return this.http.get<UserCredentialResponse[]>(`${this.base}shortener/user-credentials`, { params });
  }

  /**
   * Generate a new API key server-side – creates a new credential record.
   * The raw key is returned exactly once.
   */
  generateApiKey(userId: string, name: string): Observable<GenerateApiKeyResponse> {
    return this.http.post<GenerateApiKeyResponse>(
      `${this.base}shortener/user-credentials/generate-apikey`,
      { userId, name }
    );
  }

  /**
   * Register an externally supplied raw API key – creates a new credential record.
   * Backend stores only SHA-256(apiKey).
   */
  registerApiKey(userId: string, name: string, apiKey: string): Observable<UserCredentialResponse> {
    return this.http.post<UserCredentialResponse>(
      `${this.base}shortener/user-credentials/register-apikey`,
      { userId, name, apiKey }
    );
  }

  /** Soft-delete a credential record by its UUID. */
  deleteCredential(uuid: string): Observable<any> {
    return this.http.delete(`${this.base}shortener/user-credentials/${encodeURIComponent(uuid)}`);
  }

  /** Paginated login history – regular users see only their own records. */
  getLoginHistory(page = 1, size = 10, userId?: string): Observable<LoginHistoryResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString());
    if (userId) {
      params = params.set('user_id', userId);
    }
    return this.http.get<LoginHistoryResponse>(`${this.base}shortener/login-history`, { params });
  }
}
